// Agent Rental Marketplace — fixed-time-window pricing (frontend mirror).
// Mirrors src/utils/rentalPricing.ts (GPU tiers) but derives price from each
// agent's own pricePerCall instead of a fixed tier table.
//
// All amounts are LAMPORTS (native SOL, 9 decimals; 1 SOL = 1e9 lamports).
//
// hourlyLamports = pricePerCall * AGENT_RENTAL_HOURLY_MULTIPLIER (60)
// dailyLamports  = hourlyLamports * AGENT_RENTAL_DAILY_HOURS_EQUIV (12)
//   -> renting by the "day" is priced as 12 hours' worth, i.e. a built-in
//      bulk discount vs. renting 24 discrete hours.

import { AGENT_RENTAL_DAILY_HOURS_EQUIV, AGENT_RENTAL_HOURLY_MULTIPLIER } from '@/lib/constants';

export type RentalUnit = 'hour' | 'day';

export interface RentalWindowOption {
  unit: RentalUnit;
  count: number; // 1-4
  label: string; // "1h" .. "4h", "1d" .. "4d"
}

/** The 8 fixed-window choices offered in the rent dialog. */
export const RENTAL_WINDOW_OPTIONS: RentalWindowOption[] = [
  { unit: 'hour', count: 1, label: '1h' },
  { unit: 'hour', count: 2, label: '2h' },
  { unit: 'hour', count: 3, label: '3h' },
  { unit: 'hour', count: 4, label: '4h' },
  { unit: 'day', count: 1, label: '1d' },
  { unit: 'day', count: 2, label: '2d' },
  { unit: 'day', count: 3, label: '3d' },
  { unit: 'day', count: 4, label: '4d' },
];

const HOURLY_MULTIPLIER = Number(AGENT_RENTAL_HOURLY_MULTIPLIER); // 60
const DAILY_HOURS_EQUIV = Number(AGENT_RENTAL_DAILY_HOURS_EQUIV); // 12

/** Price of a 1-hour rental window for an agent, in lamports. */
export function hourlyLamports(pricePerCall: number): number {
  return Math.floor(pricePerCall) * HOURLY_MULTIPLIER;
}

/** Price of a 1-day rental window for an agent, in lamports (bulk discount vs 24h). */
export function dailyLamports(pricePerCall: number): number {
  return hourlyLamports(pricePerCall) * DAILY_HOURS_EQUIV;
}

export interface RentalWindowQuote {
  totalLamports: number;
  durationSeconds: number;
  windowLabel: string;
  creatorAmount: number;
  platformAmount: number;
}

/**
 * Computes the full price quote (total + creator/platform split + duration)
 * for a given rental window option, based on the agent's own pricePerCall
 * and revenue-split percentages.
 */
export function quoteRentalWindow(
  pricePerCall: number,
  creatorSplitPct: number,
  option: RentalWindowOption
): RentalWindowQuote {
  const totalLamports =
    option.unit === 'hour'
      ? hourlyLamports(pricePerCall) * option.count
      : dailyLamports(pricePerCall) * option.count;
  const durationSeconds = option.unit === 'hour' ? option.count * 3600 : option.count * 86400;
  const creatorAmount = Math.floor((totalLamports * creatorSplitPct) / 100);
  const platformAmount = totalLamports - creatorAmount;
  return { totalLamports, durationSeconds, windowLabel: option.label, creatorAmount, platformAmount };
}
